import * as React from "react";
import Accordion, { accordionClasses } from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails, {
  accordionDetailsClasses,
} from "@mui/material/AccordionDetails";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Fade from "@mui/material/Fade";
import { useLocation } from "react-router-dom";
import { Search } from "lucide-react";

export default function AccordionTransition() {
  const [expanded, setExpanded] = React.useState(null);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const location = useLocation();

  const refs = {
    central: React.useRef(null),
    compra: React.useRef(null),
    entrega: React.useRef(null),
    troca: React.useRef(null),
  };

  const faqMap = React.useMemo(
    () => ({
      central: { id: "panel10", ref: refs.central },
      compra: { id: "panel12", ref: refs.compra },
      entrega: { id: "panel3", ref: refs.entrega },
      troca: { id: "panel11", ref: refs.troca },
    }),
    [refs.central, refs.compra, refs.entrega, refs.troca]
  );

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get("section");

    if (section && faqMap[section]) {
      const { id, ref } = faqMap[section];
      setExpanded(id);
      setTimeout(() => {
        ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [location, faqMap]);

  const handleExpansion = (panel) => () => {
    setExpanded((prevExpanded) => (prevExpanded === panel ? null : panel));
  };

  const faqData = [
    {
      id: "panel1",
      title: "1. Quais são as formas de pagamento aceitas?",
      content:
        "Aceitamos cartões de crédito, débito, boleto bancário, Pix e carteiras digitais como PayPal e Mercado Pago.",
    },
    {
      id: "panel2",
      title: "2. Posso parcelar minha compra?",
      content:
        "Sim! Parcelamos em até 12x no cartão de crédito, dependendo do valor total da compra.",
    },
    {
      id: "panel3",
      title: "3. Quanto tempo leva para o produto chegar?",
      content:
        "O prazo de entrega varia conforme o CEP e o tipo de frete escolhido.",
      ref: refs.entrega,
    },
    {
      id: "panel4",
      title: "4. Meu produto veio com defeito, o que fazer?",
      content:
        "Entre em contato com nosso suporte em até 7 dias corridos após o recebimento.",
    },
    {
      id: "panel5",
      title: "5. Os produtos têm garantia?",
      content: "Sim! Todos os produtos possuem garantia mínima de 90 dias.",
    },
    {
      id: "panel6",
      title: "6. Como acompanho meu pedido?",
      content: "Você receberá um código de rastreio por e-mail.",
    },
    {
      id: "panel7",
      title: "7. Posso cancelar minha compra?",
      content: "Sim, dentro de 7 dias após o recebimento.",
    },
    {
      id: "panel8",
      title: "8. Vocês emitem nota fiscal?",
      content: "Sim, todos os pedidos são acompanhados de NF-e.",
    },
    {
      id: "panel9",
      title: "9. É possível retirar o produto na loja?",
      content: "Sim, se essa opção estiver disponível na sua região.",
    },
    {
      id: "panel10",
      title: "10. Como entro em contato com o suporte?",
      content: (
        <>
        Por e-mail:{" "} 
        <a href="mailto:dominusassistencia@gmail.com">
          dominusassistencia@gmail.com
        </a>{" "}
        ou pelo WhatsApp: {" "}
        <a
          href="https://wa.me/556184497981"
          target="_blank"
          rel="noopener noreferrer"
        >
          (61) 8449-7981
        </a>
        </>

      ),ref: refs.central
       
    },
    {
      id: "panel11",
      title: "11. Como faço para trocar um produto?",
      content: (
        <>
          1- Tire uma foto nítida do defeito do produto, para que possamos
          entender melhor a situação. <br />
          2. <strong>Entre em contato conosco</strong> por e-mail:{" "}
          <a href="mailto:dominusassistencia@gmail.com">
            dominusassistencia@gmail.com
          </a>{" "}
          ou pelo WhatsApp:{" "}
          <a
            href="https://wa.me/556184497981"
            target="_blank"
            rel="noopener noreferrer"
          >
            (61) 8449-7981
          </a>
          .<br />
        </>
      ),
      ref: refs.troca,
    },
    {
      id: "panel12",
      title: "12.Como faço para comprar um produto no site?",
      content:
        "Para comprar em nosso site, é necessário fazer login. Se for seu primeiro acesso, será preciso se registrar. Depois disso, você pode navegar pelos produtos, escolher o que deseja, adicionar ao carrinho e, ao finalizar, será direcionado automaticamente para o WhatsApp. Lá, um de nossos vendedores entrará em contato para confirmar os detalhes e concluir o pedido com você.",
      ref: refs.compra,
    },
    {
      id: "panel13",
      title: "13. Vocês entregam em todo o Brasil?",
      content: "Sim, para todas as regiões do país.",
    },
    {
      id: "panel14",
      title: "14. Posso alterar o endereço de entrega?",
      content: "Em alguns casos sim. Entre em contato com o suporte.",
    },
    {
      id: "panel25",
      title: "15. Como funciona o frete grátis?",
      content:
        "Para compras acima de determinado valor ou regiões específicas.",
    },
  ];

  const filteredFaqData = faqData.filter((faq) =>
    faq.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div>
      <div className="m-[3%]  mt-5 md:mt-24">
        <h1 className="text-2xl font-bold">Perguntas Frequentes (FAQ)</h1>
      </div>

      <div className="flex justify-center m-[1%] mb-5 md:mb-2 ">
        <div className="relative w-[95%]">
          <input
            type="text"
            placeholder="Pesquisar perguntas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className={`p-2 pl-10 border border-gray-300 rounded w-full  ${
              isFocused ? "focus:ring-2 focus:ring-green-500" : ""
            } placeholder-gray-400 focus:border-transparent focus:ring-2 focus:ring-green-500`} // Removendo a borda azul
          />
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-300"
            size={20}
          />{" "}
          {/* Ícone de lupa */}
        </div>
      </div>

      <div className="m-[3%] mt-10 md:mt-8 ">
        {filteredFaqData.map((faq) => (
          <Accordion
            key={faq.id}
            ref={faq.ref || null}
            expanded={expanded === faq.id}
            onChange={handleExpansion(faq.id)}
            slots={{ transition: Fade }}
            slotProps={{ transition: { timeout: 400 } }}
            sx={[
              expanded === faq.id
                ? {
                    [`& .${accordionClasses.region}`]: { height: "auto" },
                    [`& .${accordionDetailsClasses.root}`]: {
                      display: "block",
                    },
                  }
                : {
                    [`& .${accordionClasses.region}`]: { height: 0 },
                    [`& .${accordionDetailsClasses.root}`]: { display: "none" },
                  },
            ]}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls={`${faq.id}-content`}
              id={`${faq.id}-header`}
            >
              <Typography component="span">{faq.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>{faq.content}</Typography>
            </AccordionDetails>
          </Accordion>
        ))}
      </div>
    </div>
  );
}
